/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import documentRenderService from "../../services/documentRender/documentRenderService";
import "./DocumentAttachments.css";
import LoadingCircle from "../LoadingCircle";

export const DocumentAttachments = ({
  tabKey,
  documentAttatchmentsList,
  signerGuid,
}) => {
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(1);
  const [renderedPages, setRenderedPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actualAttachedPage, setActualAttachedPage] = useState(1);

  const observerRef = useRef(null);
  const { t } = useTranslation();

  const fetchPages = async (startPage) => {
    if (!documentAttatchmentsList[tabKey] || loading || nextPage === -1) return;

    setLoading(true);
    try {
      const { GUID } = documentAttatchmentsList[tabKey];
      const response = await documentRenderService.renderAttachmentPages(
        signerGuid,
        GUID,
        nextPage
      );

      const { NumberOfPages, NextPage, DocPageRendered } = response.data;

      setRenderedPages((prev) => [...prev, ...(DocPageRendered || [])]);
      setTotalPages(NumberOfPages || 0);
      setNextPage(NextPage || -1);
    } catch (err) {
      console.error("❌ Error al obtener páginas:", err);
    } finally {
      setLoading(false);
    }
  };

  // Reset when attachment or tab changes
  useEffect(() => {
    if (documentAttatchmentsList.length > 0) {
      setNextPage(1);
      setRenderedPages([]);
      setActualAttachedPage(1);
    }
  }, [documentAttatchmentsList, tabKey]);

  useEffect(() => {
    if (nextPage === 1) {
      fetchPages(1);
    }
  }, [nextPage]);

  // Observer for before to last image
  useEffect(() => {
    if (!observerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const lastEntry = entries[0];
        if (
          lastEntry.isIntersecting &&
          renderedPages.length < totalPages &&
          !loading
        ) {
          fetchPages(nextPage);
        }
      },
      { root: null, rootMargin: "200px", threshold: 0.1 }
    );

    observer.observe(observerRef.current);
    return () => observer.disconnect();
  }, [renderedPages, totalPages, loading]);

  // Update current page on scroll
  useEffect(() => {
    const container = document.getElementById("pdf-container-2-id");
    if (!container) return;

    const handler = () => {
      const imgs = container.querySelectorAll("img");
      let currentPage = actualAttachedPage;

      imgs.forEach((img, index) => {
        const rect = img.getBoundingClientRect();
        if (rect.top >= 0 && rect.top <= window.innerHeight * 0.5) {
          currentPage = index + 1;
        }
      });

      setActualAttachedPage(currentPage);
    };

    container.addEventListener("scroll", handler);
    return () => container.removeEventListener("scroll", handler);
  }, [renderedPages, actualAttachedPage]);

  const attachedImages = renderedPages.map((pageUrl, index) => {
    const isLast = index === renderedPages.length - 2; // before to last page for observer

    const imageSrc = `data:image/png;base64,${pageUrl}`;

    return (
      <img
        key={pageUrl}
        src={imageSrc}
        alt={`Page ${index + 1}`}
        className="attachment-page-image"
        ref={isLast ? observerRef : null}
      />
    );
  });

  return (
    <>
      {loading && (
        <div className="loading-more-container">
          <LoadingCircle />
        </div>
      )}
      {documentAttatchmentsList.length > 0 && (
        <div className="attachment-container">
          <div className="document-preview-container">
            <div className="document-preview-header-2">
              <h1 className="document-preview-title">
                {t("documents.document-attachment-preview-title")}
              </h1>
              <div className="document-preview-subcontainer">
                <h2 className="document-preview-name">
                  <b>{documentAttatchmentsList[tabKey].FileName}</b> 
                </h2>
                <span className="document-preview-count">
                  <b>{actualAttachedPage}/{totalPages || renderedPages.length}</b>
                </span>
              </div>
            </div>

            <div className="pdf-container-2" id="pdf-container-2-id">
              {attachedImages}
            </div>
          </div>
        </div>
      )}

      {documentAttatchmentsList.length === 0 && (
        <div className="document-preview-container">
          <p>{t("general.no-attachments")}</p>
        </div>
      )}
    </>
  );
};
